import {useRef} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types'

import {Grid, Heading, Link, Pillar, Text} from '@primer/react-brand/esm'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {ContentfulInlineFootnote} from '@github-ui/swp-core/components/contentful/ContentfulInlineFootnote'
import type {RichText} from '@github-ui/swp-core/schemas/contentful/richText'

import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'
import {t} from '@github-ui/swp-core/lib/utils/localization'

import {getGenericContentLink} from '../../../../brand/lib/types/contentful'
import type Assets from '../../webgl-utils/assets'

import SectionHero from '../../_components/SectionHero/SectionHero'
import SectionIntro from '../../_components/SectionIntro/SectionIntro'

import {RiverAccordion} from '../../_components/RiverAccordion/RiverAccordion'
import type {HomeAutomationContent} from '../../content'

const ANALYTICS_ID = 'automation'

type Props = {
  contentfulContent: HomeAutomationContent
  assetsRef: React.RefObject<Assets | null>
}

export function AutomationSection(props: Props) {
  const {
    contentfulContent: {
      breakout: automationSectionBreakout,
      accordionItems: accordionEntries,
      breakoutContent,
      sectionIntro,
    },
    assetsRef,
  } = props

  const breakoutVideo = automationSectionBreakout.fields.media?.find(
    item => item.fields.id === 'automationSectionBreakoutVideo',
  )

  const breakoutVideoPoster = automationSectionBreakout.fields.media?.find(
    item => item.fields.id === 'automationSectionBreakoutVideoPoster',
  )

  const accordionItems = accordionEntries.map(item => {
    const {heading, subheading, media} = item.fields
    const firstImage = media?.at(0)
    const firstLink = getGenericContentLink(item)

    return {
      title: heading || '',
      description: subheading ? documentToPlainTextString(subheading) : '',
      link: {
        label: firstLink?.fields.text || '',
        url: firstLink?.fields.href || '',
      },
      visual: {
        url: firstImage?.fields.asset.fields.file.url || '',
        alt: firstImage?.fields.description || '',
      },
    }
  })

  const mainGridRef = useRef<HTMLDivElement | null>(null)
  const {isIntersecting: isMainGridInView} = useIntersectionObserver(mainGridRef, {threshold: 0.33, isOnce: true})
  const breakoutLink = getGenericContentLink(automationSectionBreakout)

  function renderRichText(richText: RichText, fontSize: '300' | '400' = '400') {
    return documentToReactComponents(richText, {
      renderMark: {
        [MARKS.BOLD]: text => (
          <Text as="span" size={fontSize} variant="default">
            {text}
          </Text>
        ),
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <Text as="span" size={fontSize} variant="muted">
            {children}
          </Text>
        ),
        [INLINES.EMBEDDED_ENTRY]: node => {
          if (node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
            return <ContentfulInlineFootnote component={node.data.target} analyticsLocation={ANALYTICS_ID} />
          }

          return null
        },
      },
    })
  }

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <section id="automation" className="lp-Section">
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="lp-SectionTemplate">
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="lp-SectionBlock">
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className="lp-SectionBlock-content">
            {sectionIntro ? (
              <SectionIntro
                title={documentToPlainTextString(sectionIntro.fields.heading)}
                description={
                  sectionIntro.fields.description ? documentToPlainTextString(sectionIntro.fields.description) : ''
                }
                mascot="copilot"
                assetsRef={assetsRef}
                isWebGLMascotOnly
              />
            ) : null}

            {breakoutVideo ? (
              <SectionHero
                analyticsId={ANALYTICS_ID}
                isCopilotUI
                hasPlayButton
                playButtonAriaLabel={{
                  play: t('Play video'),
                  pause: t('Pause video'),
                }}
                visuals={[
                  {
                    type: 'video',
                    url: {
                      desktop: breakoutVideo.fields.asset.fields.file.url,
                    },
                    poster: {
                      desktop: breakoutVideoPoster ? breakoutVideoPoster.fields.asset.fields.file.url : '',
                    },
                    alt: breakoutVideo.fields.description ? breakoutVideo.fields.description : '',
                  },
                ]}
              />
            ) : null}
          </div>
        </div>

        {automationSectionBreakout ? (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          <div className="lp-SectionBlock">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <div ref={mainGridRef} className="lp-SectionBlock-content lp-SectionBlock-content--lines">
              {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
              <Grid className={`lp-SectionTemplate-grid ${!isMainGridInView ? 'lp-SectionTemplate-grid--hidden' : ''}`}>
                {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                <Grid.Column className="lp-SectionTemplate-grid-column" span={{xsmall: 12, medium: 7}}>
                  {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                  <div className="lp-SectionTemplate-content">
                    {automationSectionBreakout.fields.text ? (
                      <Heading as="h3" size="6" weight="semibold">
                        {renderRichText(automationSectionBreakout.fields.text)}
                      </Heading>
                    ) : null}

                    {breakoutLink ? (
                      <div>
                        <Link
                          href={breakoutLink.fields.href}
                          variant="accent"
                          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                          className="lp-SectionTemplate-content-link"
                          {...getAnalyticsEvent({
                            action: breakoutLink.fields.text,
                            tag: 'link',
                            context: 'content',
                            location: ANALYTICS_ID,
                          })}
                        >
                          <Text weight="normal" size="300">
                            {breakoutLink.fields.text}
                          </Text>
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </Grid.Column>

                {automationSectionBreakout.fields.content ? (
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                  <Grid.Column className="lp-SectionTemplate-grid-column" span={{xsmall: 12, medium: 5}}>
                    {breakoutContent.map(item => {
                      const link = getGenericContentLink(item)

                      return (
                        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                        <div key={item.sys.id} className="lp-SectionTemplate-customer">
                          <Pillar>
                            {item.fields.media?.[0]?.fields.asset.fields.file.url ? (
                              <Pillar.Image
                                src={item.fields.media[0]?.fields.asset.fields.file.url || ''}
                                alt={item.fields.media[0]?.fields.description || ''}
                                height={32}
                                loading="lazy"
                              />
                            ) : null}

                            {item.fields.text ? (
                              /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                              <Pillar.Description className="lp-SectionTemplate-customer-description">
                                {renderRichText(item.fields.text, '300')}
                              </Pillar.Description>
                            ) : null}

                            {link ? (
                              <Pillar.Link
                                /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                                className="lp-SectionTemplate-customer-link"
                                href={link.fields.href}
                                {...getAnalyticsEvent({
                                  action: link.fields.text,
                                  tag: 'link',
                                  context: 'customers',
                                  location: ANALYTICS_ID,
                                })}
                              >
                                <Text weight="normal" size="300">
                                  {link.fields.text}
                                </Text>
                              </Pillar.Link>
                            ) : null}
                          </Pillar>
                        </div>
                      )
                    })}
                  </Grid.Column>
                ) : null}
              </Grid>
            </div>
          </div>
        ) : null}

        {accordionItems.length > 0 ? <RiverAccordion items={accordionItems} analyticsId={ANALYTICS_ID} /> : null}
      </div>
    </section>
  )
}
